import React, { useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useI18n } from '../../i18n/I18nContext'
import { ContactInfo } from '../../Utils/Data'

function Header() {
  const { pathname } = useLocation()
  const { t, locale, setLocale, isRtl } = useI18n()
  const [authVersion, setAuthVersion] = useState(0)
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false)
  const isHomePage = pathname === '/' || pathname === '/index.html'
  const nextLocale = locale === 'ar' ? 'en' : 'ar'
  const nextLocaleLabel = nextLocale === 'ar' ? t('header.arabic') : t('header.english')
  const normalizedPath = pathname.replace(/\/+$/, '') || '/'
  const serviceRoutes = [
    '/services',
    '/property-management-saudi-arabia',
    '/real-estate-brokerage-saudi-arabia',
    '/real-estate-marketing-saudi-arabia',
    '/property-maintenance-operations-saudi-arabia',
    '/property-development-saudi-arabia',
  ]

  const isActivePath = (target) => {
    if (target === '/') return normalizedPath === '/'
    return normalizedPath === target || normalizedPath.startsWith(`${target}/`)
  }

  const isAboutActive = isActivePath('/about') || isActivePath('/gallery')
  const isServicesActive = serviceRoutes.some((route) => isActivePath(route))
  const isPropertyActive = isActivePath('/property') || isActivePath('/property-details')
  const isBlogsActive = isActivePath('/blogs') || isActivePath('/blogs-details')
  const isDashboardActive = isActivePath('/agent-dashboard') || isActivePath('/login')

  const currentUser = useMemo(() => {
    try {
      const rawUser = localStorage.getItem('user')
      return rawUser ? JSON.parse(rawUser) : null
    } catch {
      return null
    }
  }, [authVersion])

  const currentRole = String(currentUser?.role || '').toUpperCase()
  const isDashboardUser = currentRole === 'AGENT' || currentRole === 'ADMIN'

  useEffect(() => {
    const body = document.body
    const utilize = document.getElementById('ltn__utilize-mobile-menu')
    const overlay = document.querySelector('.ltn__utilize-overlay')
    const toggles = Array.from(document.querySelectorAll('.mobile-menu-toggle .ltn__utilize-toggle'))
    const closes = Array.from(document.querySelectorAll('.ltn__utilize-close'))

    if (!utilize || !overlay || toggles.length === 0) return undefined

    const openMenu = (event) => {
      event.preventDefault()
      body.classList.add('ltn__utilize-open')
      utilize.classList.add('ltn__utilize-open')
      overlay.style.display = 'block'
      toggles.forEach((toggle) => toggle.classList.add('close'))
    }

    const closeMenu = (event) => {
      event?.preventDefault?.()
      body.classList.remove('ltn__utilize-open')
      utilize.classList.remove('ltn__utilize-open')
      overlay.style.display = 'none'
      toggles.forEach((toggle) => toggle.classList.remove('close'))
      setMobileAboutOpen(false)
    }

    toggles.forEach((toggle) => toggle.addEventListener('click', openMenu))
    closes.forEach((closeButton) => closeButton.addEventListener('click', closeMenu))
    overlay.addEventListener('click', closeMenu)

    return () => {
      toggles.forEach((toggle) => toggle.removeEventListener('click', openMenu))
      closes.forEach((closeButton) => closeButton.removeEventListener('click', closeMenu))
      overlay.removeEventListener('click', closeMenu)
      closeMenu()
    }
  }, [pathname])

  useEffect(() => {
    setMobileAboutOpen(isAboutActive)
  }, [isAboutActive])

  useEffect(() => {
    const handleAuthChanged = () => setAuthVersion((v) => v + 1)
    window.addEventListener('storage', handleAuthChanged)
    window.addEventListener('qtech-auth-changed', handleAuthChanged)
    return () => {
      window.removeEventListener('storage', handleAuthChanged)
      window.removeEventListener('qtech-auth-changed', handleAuthChanged)
    }
  }, [])

  return (
    <>
      <header
        className={`ltn__header-area ltn__header-5 ltn__header-logo-and-mobile-menu-in-mobile ltn__header-logo-and-mobile-menu ${
          isHomePage ? 'ltn__header-transparent' : ''
        } gradient-color-4---`}
      >
        <div className="ltn__header-top-area top-area-color-white">
          <div className="container">
            <div className="row">
              <div className="col-md-7">
                <div className="ltn__top-bar-menu">
                  <ul>
                    <li>
                      <a href={`mailto:${ContactInfo.contact_email}`}>
                        <i className="icon-mail" /> {ContactInfo.contact_email}
                      </a>
                    </li>
                    <li>
                      <a href={ContactInfo.office_maps_share_url} target="_blank" rel="noreferrer">
                        <i className="icon-placeholder" /> {ContactInfo.office_address_line1}
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="col-md-5">
                <div className={`top-bar-right ${isRtl ? 'text-start' : 'text-end'}`}>
                  <div className="ltn__top-bar-menu">
                    <ul>
                      <li className="qtech-lang-switch">
                        <button
                          type="button"
                          className="qtech-lang-option"
                          onClick={() => setLocale(nextLocale)}
                          style={{ background: 'transparent', border: 0, padding: 0 }}
                        >
                          <i className="fas fa-globe" aria-hidden="true" style={{ marginRight: isRtl ? 0 : 8, marginLeft: isRtl ? 8 : 0 }} />
                          <span>{nextLocaleLabel}</span>
                        </button>
                      </li>
                      <li>
                        <div className="ltn__social-media">
                          <ul>
                            <li>
                              <a href={ContactInfo.facebook} title="Facebook" target="_blank" rel="noreferrer">
                                <i className="fab fa-facebook-f" />
                              </a>
                            </li>
                            <li>
                              <a href={ContactInfo.X} title="X" target="_blank" rel="noreferrer">
                                <i className="fab fa-twitter" />
                              </a>
                            </li>
                            <li>
                              <a href={ContactInfo.instagram} title="Instagram" target="_blank" rel="noreferrer">
                                <i className="fab fa-instagram" />
                              </a>
                            </li>
                            <li>
                              <a href={ContactInfo.tiktok} title="TikTok" target="_blank" rel="noreferrer">
                                <i className="fab fa-tiktok" />
                              </a>
                            </li>
                          </ul>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="ltn__header-middle-area ltn__header-sticky ltn__sticky-bg-black">
          <div className="container">
            <div className="row">
              <div className="col">
                <div className="site-logo-wrap">
                  <div className="site-logo">
                    <Link to="/">
                      {isHomePage ? (
                        <span className="qtech-header-logo-window">
                          <img src="/img/final-logo.svg" alt="QTECH" className="qtech-header-logo qtech-header-logo-home-final" />
                        </span>
                      ) : (
                        <>
                          <img
                            src="/img/logo-black.png"
                            alt="QTECH"
                            className="qtech-header-logo qtech-header-logo-on-light-bar"
                          />
                          <span className="qtech-header-logo-window qtech-header-logo-on-dark-bar" aria-hidden={true}>
                            <img src="/img/final-logo.svg" alt="" className="qtech-header-logo qtech-header-logo-home-final" />
                          </span>
                        </>
                      )}
                    </Link>
                  </div>
                </div>
              </div>

              <div className={`col header-menu-column ${isHomePage ? 'menu-color-white' : ''}`}>
                <div className="header-menu d-none d-xl-block">
                  <nav>
                    <div className="ltn__main-menu">
                      <ul>
                        <li className={isActivePath('/') ? 'active' : ''}>
                          <Link to="/">{t('nav.home')}</Link>
                        </li>
                        <li className={isAboutActive ? 'active' : ''}>
                          <Link to="/about">{t('nav.about')}</Link>
                          <ul className="sub-menu">
                            <li className={isActivePath('/about') ? 'active' : ''}>
                              <Link to="/about">{t('nav.aboutSubAbout')}</Link>
                            </li>
                            <li className={isActivePath('/gallery') ? 'active' : ''}>
                              <Link to="/gallery">{t('nav.gallery')}</Link>
                            </li>
                          </ul>
                        </li>
                        <li className={isServicesActive ? 'active' : ''}>
                          <Link to="/services">{t('nav.services')}</Link>
                        </li>
                        <li className={isPropertyActive ? 'active' : ''}>
                          <Link to="/property">{t('nav.property')}</Link>
                        </li>
                        <li className={isBlogsActive ? 'active' : ''}>
                          <Link to="/blogs">{t('nav.blogs')}</Link>
                        </li>
                        <li className={isActivePath('/contact') ? 'active' : ''}>
                          <Link to="/contact">{t('nav.contact')}</Link>
                        </li>
                        <li className={`special-link ${isDashboardActive ? 'active' : ''}`}>
                          {isDashboardUser ? (
                            <Link to="/agent-dashboard">Dashboard</Link>
                          ) : (
                            <Link to="/login">{t('nav.signIn')}</Link>
                          )}
                        </li>
                      </ul>
                    </div>
                  </nav>
                </div>
              </div>

              <div className="col--- ltn__header-options ltn__header-options-2">
                <div className="mobile-menu-toggle d-xl-none">
                  <a href="#ltn__utilize-mobile-menu" className="ltn__utilize-toggle" aria-label="Open mobile menu">
                    <svg viewBox="0 0 800 600">
                      <path
                        d="M300,220 C300,220 520,220 540,220 C740,220 640,540 520,420 C440,340 300,200 300,200"
                        id="top"
                      />
                      <path d="M300,320 L540,320" id="middle" />
                      <path
                        d="M300,210 C300,210 520,210 540,210 C740,210 640,530 520,410 C440,330 300,190 300,190"
                        id="bottom"
                        transform="translate(480, 320) scale(1, -1) translate(-480, -318) "
                      />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div id="ltn__utilize-mobile-menu" className="ltn__utilize ltn__utilize-mobile-menu">
        <div className="ltn__utilize-menu-inner ltn__scrollbar">
          <div className="ltn__utilize-menu-head">
            <div className="site-logo">
              <Link to="/">
                <span className="qtech-header-logo-window">
                  <img src="/img/logo-black.png" alt="QTECH" className="qtech-header-logo qtech-header-logo-home-final" />
                </span>
              </Link>
            </div>
            <button className="ltn__utilize-close" aria-label="Close mobile menu">
              ×
            </button>
          </div>

          <div className="ltn__utilize-menu">
            <ul>
              <li className={isActivePath('/') ? 'active' : ''}>
                <Link to="/">{t('nav.home')}</Link>
              </li>
              <li className={isAboutActive || mobileAboutOpen ? 'active' : ''}>
                <Link to="/about">{t('nav.about')}</Link>
                <button
                  type="button"
                  className="menu-expand"
                  aria-label="Toggle About submenu"
                  aria-expanded={mobileAboutOpen}
                  onClick={() => setMobileAboutOpen((open) => !open)}
                />
                <ul className="sub-menu" style={{ display: mobileAboutOpen ? 'block' : undefined }}>
                  <li className={isActivePath('/about') ? 'active' : ''}>
                    <Link to="/about">{t('nav.aboutSubAbout')}</Link>
                  </li>
                  <li className={isActivePath('/gallery') ? 'active' : ''}>
                    <Link to="/gallery">{t('nav.gallery')}</Link>
                  </li>
                </ul>
              </li>
              <li className={isServicesActive ? 'active' : ''}>
                <Link to="/services">{t('nav.services')}</Link>
              </li>
              <li className={isPropertyActive ? 'active' : ''}>
                <Link to="/property">{t('nav.property')}</Link>
              </li>
              <li className={isBlogsActive ? 'active' : ''}>
                <Link to="/blogs">{t('nav.blogs')}</Link>
              </li>
              <li className={isActivePath('/contact') ? 'active' : ''}>
                <Link to="/contact">{t('nav.contact')}</Link>
              </li>
              <li className={isDashboardActive ? 'active' : ''}>
                {isDashboardUser ? (
                  <Link to="/agent-dashboard">Dashboard</Link>
                ) : (
                  <Link to="/login">{t('nav.signIn')}</Link>
                )}
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="ltn__utilize-overlay" />
    </>
  )
}

export default Header
